import { z } from "zod";
import { Finite, IsoDate, IsoInstant, Symbol_ } from "./primitives";
import { SleeveId, Weights } from "./scoring";

/**
 * Virtual investor personas: simulated fake-money portfolios traded daily by
 * deterministic rules over the published snapshot. Cosmos DB is the system of
 * record (state must survive between nightly CI runs, and generated data files
 * do not); the web app reads a static artifact derived from it.
 */

export const PersonaStyle = z.enum([
  "aggressive",
  "moderate",
  "conservative",
  "day-trader",
  "discretionary",
]);
export type PersonaStyle = z.infer<typeof PersonaStyle>;

/**
 * How often a persona's rules run a full buy/sell pass.
 *
 * Two are not rule passes at all, and the nightly pass only marks and records
 * for them: `intraday` trades several times a session from live quotes
 * (`persona:tick`), and `discretionary` is decided once a day by an LLM
 * reading the snapshot (`persona:decide`).
 */
export const PersonaCadence = z.enum([
  "daily",
  "weekly",
  "deterioration",
  "intraday",
  "discretionary",
]);
export type PersonaCadence = z.infer<typeof PersonaCadence>;

/** Which risk-flag severities a persona refuses to hold. */
export const FlagTolerance = z.enum(["all", "skip-severe", "skip-caution"]);
export type FlagTolerance = z.infer<typeof FlagTolerance>;

/** Wall-clock time of day in the US market's zone, `HH:MM`, 24-hour. */
export const ClockTime = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "expected HH:MM");
export type ClockTime = z.infer<typeof ClockTime>;

/**
 * The rules an intraday persona ticks by. Fractions are of price: 0.01 is one
 * percent. Shared because both the engine config and the published artifact
 * carry them — the page states the rules it is showing the results of.
 */
export const IntradayRules = z.object({
  /** Top-N names under the persona's weights that a tick will quote. */
  watchlistSize: z.number().int().positive().max(40),
  /** Buy when a watchlist name is at least this far above its previous close. */
  entryMove: Finite.positive(),
  /** Sell when the live price is this far below the entry price. */
  stopLoss: Finite.positive(),
  /** Sell when the live price is this far above the entry price. */
  takeProfit: Finite.positive(),
  /** Sell when the live price is this far below the intraday high since entry. */
  trailingStop: Finite.positive(),
  /** No entries before this Eastern time; the open is too noisy to chase. */
  openAfter: ClockTime,
  /** From this Eastern time every position is closed and nothing is opened. */
  flattenAfter: ClockTime,
  /** A quote older than this is not a price to trade on. */
  maxQuoteAgeMinutes: z.number().positive(),
});
export type IntradayRules = z.infer<typeof IntradayRules>;

/**
 * The hard limits an LLM-decided persona trades inside. The model proposes;
 * the engine enforces these and rejects what breaks them, so the rules are
 * what the page can honestly say the persona is bound by.
 */
export const DiscretionaryRules = z.object({
  /** Top-N names under the persona's weights the model is shown and may buy from. */
  shortlistSize: z.number().int().positive().max(60),
  /** Actions executed per day; the rest are rejected in the order proposed. */
  maxTradesPerDay: z.number().int().positive().max(20),
  /** Largest single position as a fraction of equity at the time of the buy. */
  maxPositionWeight: Finite.positive().max(1),
  /** Sessions a bought name must be held before the model may sell it. */
  minHoldDays: z.number().int().nonnegative(),
});
export type DiscretionaryRules = z.infer<typeof DiscretionaryRules>;

export const PersonaSlug = z.string().regex(/^[a-z0-9-]+$/, "expected kebab-case slug");
export type PersonaSlug = z.infer<typeof PersonaSlug>;

/** Calendar month in ISO `YYYY-MM` form, the unit of one trade ledger. */
export const IsoMonth = z.string().regex(/^\d{4}-\d{2}$/, "expected YYYY-MM");
export type IsoMonth = z.infer<typeof IsoMonth>;

export const PERSONA_SCHEMA_VERSION = 1;

/**
 * Document ids double as partition keys, so every access is a point read or
 * point write at ~1 RU. Unlike the portfolio/conversation containers (which
 * partition on `/userId`), the personas container partitions on `/id`: there
 * is no user identity here — the id space is a fixed server-side set derived
 * from config slugs, never from anything a caller sends.
 */
export const personaDocId = (slug: string): string => `persona:${slug}`;
export const personaLedgerId = (slug: string, month: string): string =>
  `persona:${slug}:${month}`;

/** The trade month a trading day belongs to. */
export const monthOf = (asOf: string): string => asOf.slice(0, 7);

export const PersonaPosition = z.object({
  symbol: Symbol_,
  /** Fractional, 4 decimal places. Whole shares are degenerate at $3,000. */
  shares: Finite.positive(),
  /** Total dollars paid for the current shares, average-cost basis. */
  costBasis: Finite.nonnegative(),
  openedAsOf: IsoDate,
  /** Persona composite (0..100) on the day the position was opened. */
  scoreAtPurchase: Finite.nullable(),
  rankAtPurchase: z.number().int().positive().nullable(),
  /**
   * Most recent price the snapshot supplied. Kept so a symbol with no quote
   * today (or one that left the snapshot) can still be marked and, if needed,
   * force-sold at its last honest mark rather than at zero or a guess.
   */
  lastKnownPrice: Finite.positive(),
  lastKnownPriceAsOf: IsoDate,
  /** First day the symbol was absent from the snapshot; null while present. */
  missingSince: IsoDate.nullable(),
  /**
   * Consecutive snapshots the symbol has been absent from. Counted in runs
   * rather than calendar days because weekends and skipped runs would make
   * date arithmetic overstate the absence.
   */
  missingRuns: z.number().int().nonnegative(),
  /**
   * Highest live price seen while held, for an intraday trailing stop. Only
   * the tick engine writes it; every other position leaves it absent.
   */
  highWaterPrice: Finite.positive().optional(),
});
export type PersonaPosition = z.infer<typeof PersonaPosition>;

/** Position targets are ~5..15; the cap is slack, not a goal. */
export const MAX_POSITIONS = 25;

export const EquityPoint = z.object({
  asOf: IsoDate,
  equity: Finite,
  bank: Finite,
});
export type EquityPoint = z.infer<typeof EquityPoint>;

/**
 * Bounded so the document cannot grow without limit: 400 daily points is
 * ~19 months of history at ~45 bytes each (~18 KB), far under the 2 MB Cosmos
 * document cap. Older points fall off the front; full history survives in
 * data/history/<date>/personas.json.
 */
export const MAX_EQUITY_POINTS = 400;

/** 240 months = 20 years of ledger index. */
export const MAX_MONTHS = 240;

export const MAX_SKIPS = 20;

export const PersonaSkip = z.object({
  asOf: IsoDate,
  reason: z.string().max(200),
});
export type PersonaSkip = z.infer<typeof PersonaSkip>;

/**
 * One persona's durable state — cash, positions, running totals — updated by
 * exactly one writer (the nightly trade stage).
 */
export const PersonaPortfolioDocument = z.object({
  id: z.string().min(1),
  slug: PersonaSlug,
  schemaVersion: z.literal(PERSONA_SCHEMA_VERSION),
  name: z.string().min(1),
  style: PersonaStyle,
  /**
   * The weight vector the persona has traded under since inception, echoed
   * from config at first trade. The trade stage refuses to run when config no
   * longer matches: silently changing weights mid-stream would invalidate the
   * whole evaluation baseline the personas exist to provide.
   */
  weights: Weights,
  /** Cash. May be negative down to the persona's configured floor. */
  bank: Finite,
  startedAsOf: IsoDate,
  /** Idempotency marker: a run for asOf <= this is a no-op. */
  lastTradedAsOf: IsoDate.nullable(),
  /** Last day a weekly-cadence persona ran a full rebalance. */
  lastRebalanceAsOf: IsoDate.nullable(),
  /**
   * The most recent intraday tick that ran, for an intraday persona. A
   * separate marker from `lastTradedAsOf` because ticks run before the
   * nightly pass for the same day and must not block it. Absent on every
   * other persona and on documents written before ticks existed.
   */
  lastTickAt: IsoInstant.nullable().optional(),
  /**
   * The last snapshot day an LLM decision ran, for a discretionary persona.
   * Its own marker for the same reason as `lastTickAt`: the nightly pass sets
   * `lastTradedAsOf` before the decision stage runs.
   */
  lastDecisionAsOf: IsoDate.nullable().optional(),
  /** The model's note for that day, shown on the page as AI-written. */
  lastDecisionNote: z.string().max(600).nullable().optional(),
  positions: z.array(PersonaPosition).max(MAX_POSITIONS),
  /** Sum of realized gains and losses across all closed trades, to date. */
  realizedPnlTotal: Finite,
  equityHistory: z.array(EquityPoint).max(MAX_EQUITY_POINTS),
  /**
   * Every month that has a ledger document. Kept here so "which months exist"
   * is a point read on this document, never a container query — a query would
   * change the free tier's flat cost model to a proportional one.
   */
  months: z.array(IsoMonth).max(MAX_MONTHS),
  /** Days trading was skipped (thin snapshot etc.), most recent kept. */
  skips: z.array(PersonaSkip).max(MAX_SKIPS),
  updatedAt: z.string(),
});
export type PersonaPortfolioDocument = z.infer<typeof PersonaPortfolioDocument>;

export interface BuildPersonaPortfolioInput {
  slug: string;
  name: string;
  style: PersonaStyle;
  weights: Weights;
  bank: number;
  startedAsOf: string;
  lastTradedAsOf?: string | null;
  lastRebalanceAsOf?: string | null;
  lastTickAt?: string | null;
  lastDecisionAsOf?: string | null;
  lastDecisionNote?: string | null;
  positions?: PersonaPosition[];
  realizedPnlTotal?: number;
  equityHistory?: EquityPoint[];
  months?: IsoMonth[];
  skips?: PersonaSkip[];
}

export function buildPersonaPortfolio(input: BuildPersonaPortfolioInput): PersonaPortfolioDocument {
  return PersonaPortfolioDocument.parse({
    id: personaDocId(input.slug),
    slug: input.slug,
    schemaVersion: PERSONA_SCHEMA_VERSION,
    name: input.name,
    style: input.style,
    weights: input.weights,
    bank: input.bank,
    startedAsOf: input.startedAsOf,
    lastTradedAsOf: input.lastTradedAsOf ?? null,
    lastRebalanceAsOf: input.lastRebalanceAsOf ?? null,
    lastTickAt: input.lastTickAt ?? null,
    lastDecisionAsOf: input.lastDecisionAsOf ?? null,
    lastDecisionNote: input.lastDecisionNote ?? null,
    positions: input.positions ?? [],
    realizedPnlTotal: input.realizedPnlTotal ?? 0,
    // Oldest points fall off first; the recent curve is the story.
    equityHistory: (input.equityHistory ?? []).slice(-MAX_EQUITY_POINTS),
    months: input.months ?? [],
    skips: (input.skips ?? []).slice(-MAX_SKIPS),
    updatedAt: new Date().toISOString(),
  });
}

export const TradeSide = z.enum(["buy", "sell"]);
export type TradeSide = z.infer<typeof TradeSide>;

export const TradeReason = z.enum([
  "BOOTSTRAP", // first-ever buys from starting capital
  "ENTER_TARGET", // symbol entered the persona's target set
  "EXIT_TARGET", // symbol fell out of the persona's target set
  "REBALANCE", // weekly-cadence target refresh
  "DETERIORATION", // score/rank fell past the persona's exit thresholds
  "FLAG_INTOLERANCE", // holding acquired a flag the persona refuses to hold
  "LEFT_UNIVERSE", // symbol vanished from the snapshot; forced exit
  // Intraday cadence only — emitted by the tick engine, never by tradeDay.
  "INTRADAY_ENTRY", // watchlist name moved up past the entry threshold
  "STOP_LOSS", // live price fell past the stop below entry
  "TAKE_PROFIT", // live price rose past the target above entry
  "TRAILING_STOP", // live price fell past the trail below the intraday high
  "END_OF_DAY", // flattened before the close, or a leftover from a prior day
  // Discretionary cadence only — proposed by the LLM, validated and filled by
  // the engine at the close.
  "LLM_BUY",
  "LLM_SELL",
]);
export type TradeReason = z.infer<typeof TradeReason>;

/**
 * One executed trade, with the scoring context captured at the moment of the
 * trade. That context is the feature's transparency promise: the history shows
 * not just what a persona did but what the engine was saying when it did it.
 *
 * `sleeveContributions` stores six numbers, not the full Sleeve objects with
 * their 22 factor inputs — the full form would multiply ledger size ~50x for
 * detail the trade view never renders.
 */
export const TradeRecord = z.object({
  asOf: IsoDate,
  /**
   * The instant an intraday trade filled — the tick that made it.
   * Absent on every close-filled trade: `.optional()` rather than a default,
   * so a legacy record never comes back carrying a claim its writer did not
   * make (the same reasoning as `SimTradeRecord.priceSource`).
   */
  at: IsoInstant.optional(),
  side: TradeSide,
  symbol: Symbol_,
  shares: Finite.positive(),
  price: Finite.positive(),
  /** shares * price, rounded to cents. */
  value: Finite.positive(),
  reason: TradeReason,
  /** True when the rules forced the trade regardless of scores. */
  forced: z.boolean(),
  /** Persona-weighted composite at trade time. Null on forced exits. */
  personaComposite: Finite.nullable(),
  personaRank: z.number().int().positive().nullable(),
  sleeveContributions: z.record(SleeveId, Finite.nullable()).nullable(),
  riskFlags: z.array(
    z.object({ id: z.string(), severity: z.enum(["info", "caution", "severe"]) }),
  ),
  /** Realized gain/loss vs average-cost basis. Null on buys. */
  realizedPnl: Finite.nullable(),
  /** Cash after this trade settled, for auditability of the sequence. */
  bankAfter: Finite,
  /**
   * The model's stated reason, for an LLM-decided trade. Shown on the page as
   * AI-written. Optional so every rule-made trade stays exactly as it was.
   */
  rationale: z.string().max(300).optional(),
});
export type TradeRecord = z.infer<typeof TradeRecord>;

/**
 * Hard cap per monthly ledger. The daily trade stage caps trades at 2x the
 * position target per persona per day, so a close-traded month tops out near
 * 460 for the busiest persona. An intraday persona trades each watchlist name
 * once a day (one buy, one sell), so a 20-name watchlist bounds it at 40
 * a day, ~920 a month. At ~650 bytes a record the cap below is ~1.3 MB,
 * inside the 2 MB document limit with room for commentary. Hitting the cap
 * is a bug, and the builder throws rather than truncating: a trade ledger
 * that silently drops trades is worse than one that fails. The tick engine
 * stops trading for the month before it gets here (see `LEDGER_HEADROOM`).
 */
export const MAX_LEDGER_TRADES = 2000;

/**
 * How many ledger slots an intraday tick leaves free before it refuses to
 * open new positions: enough to flatten a full book and record the exits.
 */
export const LEDGER_HEADROOM = 60;

export const PersonaCommentary = z.object({
  asOf: IsoDate,
  /** In-character diary note. Bounded; the LLM stage truncates, not rejects. */
  text: z.string().min(1).max(500),
});
export type PersonaCommentary = z.infer<typeof PersonaCommentary>;

/** One note per trading day; a month has at most 23 sessions. */
export const MAX_LEDGER_COMMENTARY = 31;

/** One persona-month of trades and daily commentary. */
export const PersonaLedgerDocument = z.object({
  id: z.string().min(1),
  slug: PersonaSlug,
  month: IsoMonth,
  schemaVersion: z.literal(PERSONA_SCHEMA_VERSION),
  trades: z.array(TradeRecord).max(MAX_LEDGER_TRADES),
  commentary: z.array(PersonaCommentary).max(MAX_LEDGER_COMMENTARY),
  updatedAt: z.string(),
});
export type PersonaLedgerDocument = z.infer<typeof PersonaLedgerDocument>;

export function buildPersonaLedger(
  slug: string,
  month: string,
  trades: TradeRecord[],
  commentary: PersonaCommentary[] = [],
): PersonaLedgerDocument {
  return PersonaLedgerDocument.parse({
    id: personaLedgerId(slug, month),
    slug,
    month,
    schemaVersion: PERSONA_SCHEMA_VERSION,
    trades,
    commentary,
    updatedAt: new Date().toISOString(),
  });
}

// ---------------------------------------------------------------------------
// Published artifact — what the web app reads (data/latest/personas.json)
// ---------------------------------------------------------------------------

/** Trades shown on the page; full history stays in Cosmos and data/history. */
export const MAX_RECENT_TRADES = 50;

export const PersonaArtifactPosition = PersonaPosition.extend({
  /** shares * lastKnownPrice, rounded to cents. */
  marketValue: Finite,
  unrealizedPnl: Finite,
});
export type PersonaArtifactPosition = z.infer<typeof PersonaArtifactPosition>;

export const PersonaArtifactEntry = z.object({
  slug: PersonaSlug,
  name: z.string().min(1),
  style: PersonaStyle,
  /** One-line character description for the page header. */
  tagline: z.string().max(200),
  weights: Weights,
  /** Echo of the trade rules so the page can state them honestly. */
  rules: z.object({
    positionsTarget: z.number().int().positive(),
    leverage: Finite.positive(),
    bankFloor: Finite,
    cadence: PersonaCadence,
    flagTolerance: FlagTolerance,
    /**
     * Present only for an intraday persona. Optional rather than nullable so
     * an artifact written before the field existed still parses — the site
     * deploys against whatever personas.json production already serves.
     */
    intraday: IntradayRules.optional(),
    /** Present only for a discretionary persona. Same optionality as `intraday`. */
    discretionary: DiscretionaryRules.optional(),
  }),
  bank: Finite,
  positionsValue: Finite,
  equity: Finite,
  realizedPnlTotal: Finite,
  unrealizedPnl: Finite,
  /** (equity - startingCapital) / startingCapital. A fact, not a forecast. */
  returnSinceInception: Finite,
  startedAsOf: IsoDate,
  lastTradedAsOf: IsoDate.nullable(),
  /** Most recent intraday tick, for an intraday persona. Same optionality as `rules.intraday`. */
  lastTickAt: IsoInstant.nullable().optional(),
  /** The model's most recent day note, for a discretionary persona. */
  latestDecision: z
    .object({ asOf: IsoDate, note: z.string().max(600) })
    .nullable()
    .optional(),
  positions: z.array(PersonaArtifactPosition).max(MAX_POSITIONS),
  equityHistory: z.array(EquityPoint).max(MAX_EQUITY_POINTS),
  recentTrades: z.array(TradeRecord).max(MAX_RECENT_TRADES),
  latestCommentary: PersonaCommentary.nullable(),
  /** Reason today's trading was skipped, or null when it ran. */
  skippedToday: z.string().max(200).nullable(),
});
export type PersonaArtifactEntry = z.infer<typeof PersonaArtifactEntry>;

export const PERSONAS_ARTIFACT_SCHEMA_VERSION = 1;

export const PersonasArtifact = z.object({
  schemaVersion: z.literal(PERSONAS_ARTIFACT_SCHEMA_VERSION),
  generatedAt: z.string(),
  asOfDate: IsoDate,
  startingCapital: Finite.positive(),
  personas: z.array(PersonaArtifactEntry),
});
export type PersonasArtifact = z.infer<typeof PersonasArtifact>;
